import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaBook, FaProjectDiagram, FaCalculator, FaMoneyBillWave, FaClipboardCheck, FaDraftingCompass, FaInfoCircle } from 'react-icons/fa';

const MermaidDiagram = ({ code }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (window.mermaid && ref.current) {
            // Reset for re-renders - ensure we have the source code with newlines
            ref.current.removeAttribute('data-processed');
            // Explicitly set innerHTML to code to ensure fresh start
            ref.current.innerHTML = code;
            
            try {
                window.mermaid.init(undefined, ref.current);
            } catch (e) {
                console.error("Mermaid init error:", e);
            }
        }
    }, [code]);

    return (
        <div ref={ref} className="mermaid bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto text-center my-4">
            {code}
        </div>
    );
};

const TrainingDocs = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [enabledPlays, setEnabledPlays] = useState([]);
    const [loading, setLoading] = useState(true);

    const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
    const token = sessionStorage.getItem("userToken");

    useEffect(() => {
        if (window.mermaid) {
            window.mermaid.initialize({
                startOnLoad: true,
                theme: 'base',
                securityLevel: 'loose',
                themeVariables: {
                    primaryColor: '#1e5045',
                    primaryTextColor: '#ffffff',
                    primaryBorderColor: '#1e5045',
                    lineColor: '#1e5045',
                    secondaryColor: '#f26522',
                    tertiaryColor: '#f6f4ed',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif'
                }
            });
        }
    }, []);

    useEffect(() => {
        const fetchEnabledPlays = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/user/admin/org/plays/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const enabled = res.data.enabled_plays || [];
                setEnabledPlays(enabled);
            } catch (err) {
                console.error("Error fetching enabled plays", err);
                // Fallback: show all if we can't fetch status
                setEnabledPlays(['tariff_v1', 'cost_reduction_v1', 'veeco_design_review_v1', 'veeco_drawing_checker_v1', 'veeco_drawing_checker_veeco_v1', 'veeco_drawing_checker_general_v1', 'veeco_drawing_checker_v2', 'weight_v1']);
            } finally {
                setLoading(false);
            }
        };

        fetchEnabledPlays();
    }, [BASE_URL, token]);

    const isPlayEnabled = (playId) => {
        if (playId === 'checker') {
            return enabledPlays.some(p => p.includes('drawing_checker'));
        }
        return enabledPlays.includes(playId);
    };

    const allTabs = [
        { id: 'overview', label: 'Overview', icon: <FaInfoCircle />, alwaysShow: true },
        { id: 'flow', label: 'User Flow', icon: <FaProjectDiagram />, alwaysShow: true },
        { id: 'tariff', label: 'Tariff Analysis', icon: <FaCalculator />, playId: 'tariff_v1' },
        { id: 'cost', label: 'Cost Reduction', icon: <FaMoneyBillWave />, playId: 'cost_reduction_v1' },
        { id: 'design', label: 'Design Review', icon: <FaClipboardCheck />, playId: 'veeco_design_review_v1' },
        { id: 'checker_both', label: 'Checker - Both', icon: <FaDraftingCompass />, playId: 'veeco_drawing_checker_v1' },
        { id: 'checker_v2', label: 'Checker V2', icon: <FaDraftingCompass />, playId: 'veeco_drawing_checker_v2' },
        { id: 'checker_veeco', label: 'Checker - Veeco', icon: <FaDraftingCompass />, playId: 'veeco_drawing_checker_veeco_v1' },
        { id: 'checker_general', label: 'Checker - General', icon: <FaDraftingCompass />, playId: 'veeco_drawing_checker_general_v1' },
        { id: 'weight', label: 'Weight Estimation', icon: <FaCalculator />, playId: 'weight_v1' }
    ];

    const tabs = allTabs.filter(tab => tab.alwaysShow || isPlayEnabled(tab.playId));

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50">
                <div className="text-gray-500 text-xl font-medium">Loading training modules...</div>
            </div>
        );
    }

    const playDetails = {
        'tariff_v1': {
            name: 'Tariff Analysis',
            role: 'Trade Compliance Officer & Materials Scientist',
            value: 'Automates HTS classification & calculates total landed cost.'
        },
        'cost_reduction_v1': {
            name: 'Cost Reduction',
            role: 'Senior Value Engineer & Supply Chain Analyst',
            value: 'Identifies savings in design (Engineering) vs. sourcing (Supply Chain).'
        },
        'veeco_design_review_v1': {
            name: 'Design Review',
            role: 'Audit Architect & Senior Mechanical Designer',
            value: 'Two-stage "Gatekeeper" review to prevent manufacturing defects before release.'
        },
        'veeco_drawing_checker_v1': {
            name: 'Checker - Both',
            role: 'Senior Quality Control Manager',
            value: 'Parallel execution of Veeco & General standards validation.'
        },
        'veeco_drawing_checker_veeco_v1': {
            name: 'Checker - Veeco',
            role: 'Veeco Standards Auditor',
            value: 'Validation against Veeco internal documents (3EN7347, PRS179).'
        },
        'veeco_drawing_checker_general_v1': {
            name: 'Checker - General',
            role: 'Senior Mechanical Engineer',
            value: 'Validation against ASME Y14.5 and general DFM principles.'
        },
        'veeco_drawing_checker_v2': {
            name: 'Checker V2',
            role: 'Senior Quality Control Manager (Vision-Native)',
            value: 'Vision-native drawing validation using SourceOptima AI agentic vision with code execution for annotation, review, and conditional re-annotation.'
        },
        'weight_v1': {
            name: 'Weight Estimation',
            role: 'Senior Mechanical Estimator',
            value: 'AI-powered mass estimation from drawings using SourceOptima Agentic AI with specialist routing and adversarial review.'
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="space-y-6">
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-2xl font-bold text-gray-800">SourceOptima Analysis Plays</h2>
                            <p className="text-gray-700 mt-2">
                                SourceOptima converts static engineering drawings into actionable business intelligence. 
                                Instead of manually reviewing PDFs, you assign "AI Experts" (or Plays) to analyze your files. 
                                Each play acts as a specialized virtual employee—from a Trade Compliance Officer to a Senior Value Engineer.
                            </p>
                        </div>
                        
                        <div className="flex justify-center my-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <div className="text-center">
                                <img src="/training_screenshots/1_org_chart.png" alt="Organization Chart" className="border rounded-lg shadow-lg max-h-96 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 italic">Figure 1: Organization & Space selection allows teams to collaborate securely.</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                                <thead className="bg-[#1E5045] text-white">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Play Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">The "AI Expert" Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Business Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 text-sm">
                                    {tabs.filter(t => !t.alwaysShow).map(tab => {
                                        const details = playDetails[tab.playId];
                                        if (!details) return null;
                                        return (
                                            <tr key={tab.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-bold text-gray-900">{details.name}</td>
                                                <td className="px-6 py-4">{details.role}</td>
                                                <td className="px-6 py-4">{details.value}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            case 'flow':
                return (
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">User Interaction Flow</h2>
                            <p className="text-gray-600 mb-6">The platform follows a simple "Upload → Select → Analyze" workflow. The complexity of reading the drawing and applying rules is handled entirely by the AI.</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6">
                                <div>
                                    <div className="bg-white p-2 border rounded-lg shadow-sm h-full">
                                        <h4 className="font-semibold text-center mb-2 bg-gray-100 py-1 rounded">1. Select Project</h4>
                                        <img src="/training_screenshots/2_project_list.png" alt="Project Selection" className="border rounded w-full" />
                                        <p className="text-xs text-gray-500 mt-2 px-2">Choose your project folder to access uploaded engineering drawings.</p>
                                    </div>
                                </div>
                                <div>
                                    <div className="bg-white p-2 border rounded-lg shadow-sm h-full">
                                        <h4 className="font-semibold text-center mb-2 bg-gray-100 py-1 rounded">2. Workspace & Analysis</h4>
                                        <img src="/training_screenshots/3_workspace_view.png" alt="Workspace Analysis" className="border rounded w-full" />
                                        <p className="text-xs text-gray-500 mt-2 px-2">Select files, choose an "Analysis Play", and click Run. Results appear in the File Detail view.</p>
                                    </div>
                                </div>
                            </div>

                            <MermaidDiagram code={`
graph TD
    A["Start"] --> B["Login & Select Space"]
    B --> C["Open Project Folder"]
    C --> D["Select Drawings to Analyze"]
    
    subgraph Analysis Configuration
    D --> E{"Choose Play"}
    E -->|Tariff| F["Compliance Analysis"]
    E -->|Cost| G["Value Engineering"]
    E -->|Design| H["Gatekeeper Review"]
    end
    
    F --> I["Run Analysis"]
    G --> I
    H --> I
    
    I --> J["AI Process & Reasoning"]
    J --> K["View Results & Report"]
                            `} />
                        </div>
                    </div>
                );
            case 'tariff':
                return (
                    <div className="space-y-6">
                        <div className="bg-blue-50 border-l-4 border-blue-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play 1: Tariff Analysis</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Trade Compliance Officer & Materials Scientist</p>
                        </div>
                        
                        <p className="text-gray-700">
                            This play automates the complex task of customs classification. It reads the separate BOM, title block, and notes to find the materials and manufacturing method, then references the official <strong>Harmonized Tariff Schedule (HTS)</strong>.
                        </p>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B["AI: Extract Material & Specs"]
    B --> C{"Weight Explicitly Stated?"}
    C -- Yes --> D["Use Listed Weight"]
    C -- No --> E["Calculated: Geometry x Density"]
    D --> F["AI: Determine HTS Code"]
    E --> F
    F --> G["Check Section 301 'China List'"]
    F --> H["Check Section 232 'Steel/Alum'"]
    G --> I["Calculate Total Landed Cost"]
    H --> I
                        `} />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                    <h3 className="font-bold text-gray-800 mb-2 border-b pb-2">What the AI Does</h3>
                                    <ul className="list-disc pl-5 space-y-2 text-gray-600 text-sm">
                                        <li><strong>Reads Technical Data:</strong> Extracts material (e.g., "Aluminum 6061-T6") and specs.</li>
                                        <li><strong>Finds or Calculates Weight:</strong> It searches for an extracted weight first. If missing, it calculates it based on volume and density—critical for logistics costs.</li>
                                        <li><strong>Classifies Goods:</strong> Determines the 10-digit HTS code based on form and function.</li>
                                        <li><strong>Applies Trade Rules:</strong> Checks critical tariffs like Section 301 (China) and Section 232 (Steel/Aluminum).</li>
                                    </ul>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wide mb-2">Assembly Mode</h3>
                                    <p className="text-sm text-blue-800">
                                        For complex products, check <strong>"Treat as Assembly"</strong>. The AI will roll up costs from all child parts into the Top-Level Parent drawing to give a total landed cost for the finished good.
                                    </p>
                                </div>
                            </div>
                            
                            <div>
                                <h4 className="font-semibold text-gray-700 mb-2">Workspace Controls</h4>
                                <img src="/training_screenshots/3_workspace_view.png" alt="Workspace" className="border rounded shadow-md w-full" />
                            </div>
                        </div>
                    </div>
                );
            case 'cost':
                return (
                    <div className="space-y-6">
                        <div className="bg-green-50 border-l-4 border-green-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play 2: Cost Reduction</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Senior Value Engineer & Supply Chain Analyst</p>
                        </div>

                        <p className="text-gray-700">
                            This play doesn't just look for "cheaper parts." It generates a <strong>bespoke analysis strategy</strong> for every single component. 
                            It knows that a machined bracket needs different optimization than an injection-molded housing.
                        </p>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B["AI: Component Recognition"]
    B --> C["AI: Create Custom Analysis Strategy"]
    C --> D["Execute Strategic Analysis"]
    D --> E{"Categorize Findings"}
    E --> F["ENGINEERING: Design Changes"]
    E --> G["SUPPLY CHAIN: Sourcing Actions"]
    F --> H["Impact: Low/Med/High"]
    G --> H
    H --> I["Actionable Opportunities Report"]
                        `} />
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs">Category 1</span>
                                    Engineering Opportunities
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">Requires an ECO (Engineering Change Order) or design update.</p>
                                <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                                    <li>Relaxing tight tolerances that don't impact fit.</li>
                                    <li>Removing features that require expensive tooling.</li>
                                    <li>Changing materials to industry standards.</li>
                                    <li>Simplifying geometry for faster machining.</li>
                                </ul>
                            </div>

                            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs">Category 2</span>
                                    Supply Chain Opportunities
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">Executed by procurement without changing the design.</p>
                                <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                                    <li>Vendor negotiation strategies.</li>
                                    <li>Leveraging standard stock sizes.</li>
                                    <li>Process consolidation guidance for vendors.</li>
                                    <li>Alternate sourcing regions.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                );
            case 'design':
                return (
                    <div className="space-y-6">
                        <div className="bg-purple-50 border-l-4 border-purple-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play 3: Design Review</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Audit Architect (Stage 1) & Senior Designer (Stage 2)</p>
                        </div>
                        
                        <p className="text-gray-700">
                             Unlike simple "spellcheckers", this play uses a <strong>Two-Stage Expert Review</strong> process that mimics a high-level manual design review. It ensures that no boilerplate checklist is used; everything is tailored to the specific drawing.
                        </p>
                        
                        <MermaidDiagram code={`
graph TD
    subgraph "Stage 1: The Audit Architect"
    A["PDF Input"] --> B["AI: Scan ALL Pages"]
    B --> C["AI: Identify Part Type & Process"]
    C --> D["Generate Bespoke Checklist"]
    end
    
    subgraph "Stage 2: The Senior Designer"
    D --> E["AI: Review Every Feature"]
    E --> F["Verify Dimensions & Notes"]
    F --> G["Pass/Fail with Evidence"]
    end
                        `} />

                        <div className="space-y-4">
                            <div className="bg-white p-6 rounded-lg border border-gray-200">
                                <h3 className="font-bold text-purple-800 mb-3">How the "Two-Stage" Process Works</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">1. The Audit Architect</h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                            First, an AI acts as a 25-year veteran architect. It scans every page to understand if this is a weldment, a casting, or a machined part. It then writes a <strong>custom checklist</strong> specific to that part type and selects relevant Veeco Standards.
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800">2. The Senior Designer</h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                            A second AI then takes that checklist and mechanically reviews the drawing. It provides a strict <strong>PASS/FAIL</strong> for each item, citing the specific value or note on the drawing as evidence.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'checker_both':
                return (
                    <div className="space-y-6">
                        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Checker (Consolidated)</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Senior Quality Control Manager</p>
                        </div>
                        
                        <p className="text-gray-700">
                            This is the most comprehensive validation play. It runs <strong>two distinct AI agents in parallel</strong>—one focused purely on Veeco Internal Standards and one focused on General Industry Engineering Standards—and consolidates their findings into a single report.
                        </p>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B{"Parallel Execution"}
    B --> C["Agent A: Veeco Specialist"]
    B --> D["Agent B: Industry Specialist"]
    C --> E["Veeco Issues"]
    D --> F["General/DFM Issues"]
    E --> G["Consolidator AI"]
    F --> G
    G --> H["Deduplicate & Score"]
    H --> I["Final Quality Report"]
                        `} />

                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 mb-2">Why use the Consolidated Checker?</h3>
                            <p className="text-sm text-gray-600 mb-4">It provides the "belt and suspenders" approach. You get the strict compliance checking of the Veeco play plus the broader good-practice advice of the General play.</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-3 bg-gray-50 rounded border">
                                    <h4 className="font-semibold text-sm mb-1">From Veeco Agent</h4>
                                    <ul className="list-disc pl-4 text-xs text-gray-600">
                                        <li>File naming conventions (3EN7347)</li>
                                        <li>Title block format compliance</li>
                                        <li>Specific process specs (PRS179)</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-gray-50 rounded border">
                                    <h4 className="font-semibold text-sm mb-1">From General Agent</h4>
                                    <ul className="list-disc pl-4 text-xs text-gray-600">
                                        <li>ASME Y14.5 GD&T syntax</li>
                                        <li>Missing dimensions</li>
                                        <li>Manufacturability (DFM)</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'checker_v2':
                return (
                    <div className="space-y-6">
                        <div className="bg-violet-50 border-l-4 border-violet-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Checker V2</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Senior Quality Control Manager (Enhanced)</p>
                        </div>

                        <p className="text-gray-700">
                            V2 builds on the <strong>Consolidated (Both)</strong> checker with six new capabilities. It runs the same parallel Veeco + General agent pipeline, then adds post-processing intelligence on top.
                        </p>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-sm">
                            <h3 className="font-bold text-yellow-800 mb-1">V1 vs V2 — What Changed?</h3>
                            <p className="text-yellow-900">
                                V1 remains available and unchanged. V2 is a separate play you select from the dropdown. Both can run on the same file independently.
                            </p>
                        </div>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B{"Parallel Execution"}
    B --> C["Agent A: Veeco Specialist"]
    B --> D["Agent B: Industry Specialist"]
    C --> E["Veeco Issues"]
    D --> F["General/DFM Issues"]
    E --> G["Consolidator AI"]
    F --> G
    G --> H["Confidence Recalibration"]
    H --> J["Quality Report Ready"]
    H --> K["SourceOptima AI Vision Annotation"]
    K --> L["Annotated Drawings Available"]
    M["User Profile"] -.-> B
    J -.-> N["Feedback Loop"]
    N -.-> M
                        `} />

                        <h3 className="text-lg font-bold text-gray-800 mt-4">Core Features (from V1)</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Dual-Agent Analysis</h4>
                                <p className="text-xs text-gray-600">
                                    Two specialized SourceOptima AI agents run in parallel — one focused on <strong>Veeco internal standards</strong> (PRS179, 3EN7347, title block compliance) and one on <strong>general industry standards</strong> (ASME Y14.5, ISO, GD&T, DFM).
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>AI Consolidation</h4>
                                <p className="text-xs text-gray-600">
                                    A consolidator AI merges findings from both agents, deduplicates overlapping issues, and produces a single unified issue list with consistent formatting.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Severity Classification</h4>
                                <p className="text-xs text-gray-600">
                                    Every issue is classified as <strong className="text-red-600">CRITICAL</strong>, <strong className="text-orange-600">MAJOR</strong>, <strong className="text-green-600">MINOR</strong>, or <strong className="text-gray-500">INFO</strong> — helping you prioritize what to fix first.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Standard References</h4>
                                <p className="text-xs text-gray-600">
                                    Issues cite specific standards (ASME Y14.5, ISO 2768, Veeco specs) so you can trace each finding back to its source requirement.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Full HTML Quality Report</h4>
                                <p className="text-xs text-gray-600">
                                    A comprehensive, printable report with executive summary, issue cards, severity breakdown, and drawing preview — viewable in a new browser tab.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Multi-Page PDF Support</h4>
                                <p className="text-xs text-gray-600">
                                    Handles multi-page engineering drawings. Each page is processed and all findings are consolidated into a single report.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Excel Export</h4>
                                <p className="text-xs text-gray-600">
                                    Download all findings as a structured Excel spreadsheet (.xlsx) for offline review, tracking, or integration with your existing quality workflows.
                                </p>
                            </div>
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 mt-4">The 7 New Features in V2</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>1. Confidence Labels</h4>
                                <p className="text-xs text-gray-600">
                                    V1 shows raw confidence percentages (e.g., "85% conf") that are not calibrated. V2 replaces them with evidence-based labels:
                                </p>
                                <ul className="list-disc pl-4 text-xs text-gray-600 mt-1">
                                    <li><strong className="text-green-700">Verified</strong> — cites a standard AND flagged by both agents</li>
                                    <li><strong className="text-blue-700">Likely</strong> — cites a standard OR flagged by both agents</li>
                                    <li><strong className="text-gray-500">Possible</strong> — single agent, no standard cited</li>
                                </ul>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>2. User Personalization</h4>
                                <p className="text-xs text-gray-600">
                                    V2 learns what matters to you. Every time you rate an issue or drill down into one, the system records that signal and uses it to adjust future analyses.
                                </p>
                                <ul className="list-disc pl-4 text-xs text-gray-600 mt-2 space-y-1">
                                    <li><strong>Categories you find useful</strong> get more detailed rationale and fixes</li>
                                    <li><strong>Categories you dismiss</strong> get filtered at lower severities (CRITICAL/MAJOR are never filtered)</li>
                                    <li><strong>Topics you drill into</strong> get more specific standard references</li>
                                </ul>
                                <p className="text-xs text-gray-400 mt-2">First run: no personalization. After a few reviews with feedback, results start adapting. No setup required.</p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>3. Feedback Loop</h4>
                                <p className="text-xs text-gray-600">
                                    Each issue card shows three buttons: <strong>👍 Useful</strong>, <strong>😐 Neutral</strong>, <strong>👎 Not Useful</strong>. Feedback is saved instantly — no submit button needed.
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Your ratings are tallied by category. Once a category has enough ratings, the system classifies it as high-value or low-value and adjusts accordingly. Rating just a few issues per analysis is enough.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>4. Drill-Down</h4>
                                <p className="text-xs text-gray-600">
                                    Click <strong>🔍 Drill Down</strong> on any issue for a focused deep-dive: root cause, step-by-step fix, standard references, and impact assessment.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>5. Additive Manufacturing</h4>
                                <p className="text-xs text-gray-600">
                                    V2 adds AM-specific checks: build orientation, support structures, layer adhesion, powder removal, and post-processing. These only fire for AM-relevant parts.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>6. Agentic Vision Annotation</h4>
                                <p className="text-xs text-gray-600">
                                    V1 uses OCR bounding boxes (often misaligned). V2 uses SourceOptima AI vision to locate issues on the drawing, then draws deterministic annotations with consistent colors per severity.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>7. Component Context</h4>
                                <p className="text-xs text-gray-600">
                                    Describe what a part does (e.g., <em>"Wafer chuck that holds 200mm substrates at 600°C in a CVD chamber"</em>) and the analysis adapts — flagging thermal, material, and environment-specific issues it would otherwise miss. Auto-saves when you click away.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mt-4">
                            <h3 className="font-bold text-gray-800 mb-2">How to Use V2</h3>
                            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                                <li>Select files in your workspace and choose <strong>Drawing Checker V2</strong> from the play dropdown.</li>
                                <li>Run the analysis. Results appear under the <strong>Drawing Checker V2</strong> tab (indigo theme).</li>
                                <li>Review issues — note the confidence labels (Verified / Likely / Possible) instead of percentages.</li>
                                <li>Use the <strong>feedback buttons</strong> on each issue to mark what's useful. This trains the system for your next run.</li>
                                <li>Click <strong>🔍 Drill Down</strong> on critical issues for a detailed root-cause analysis.</li>
                                <li>Download the annotated drawing to see issues marked directly on the drawing pages.</li>
                            </ol>
                        </div>

                    </div>
                );
            case 'checker_veeco':
                return (
                    <div className="space-y-6">
                        <div className="bg-red-50 border-l-4 border-red-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Checker - Veeco</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Veeco Standards Auditor</p>
                        </div>

                        <p className="text-gray-700">
                            A highly specialized auditor that <strong>only</strong> cares about compliance with Veeco's internal Engineering Standards. It ignores general "good practice" unless it violates a specific Veeco document.
                        </p>

                        <div className="flex justify-center my-4">
                            <div className="bg-white p-4 rounded border shadow-sm w-full max-w-2xl">
                                <h3 className="font-bold text-gray-800 border-b pb-2 mb-3">Key Standards Verified</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600">
                                    <div className="flex justify-between"><span>Part Numbering & Titles</span> <span className="font-mono text-gray-800">3EN7347</span></div>
                                    <div className="flex justify-between"><span>General Tolerances</span> <span className="font-mono text-gray-800">PRS179</span></div>
                                    <div className="flex justify-between"><span>Vacuum Components</span> <span className="font-mono text-gray-800">PRS94</span></div>
                                    <div className="flex justify-between"><span>O-Ring Grooves</span> <span className="font-mono text-gray-800">PRS100</span></div>
                                    <div className="flex justify-between"><span>Sheet Metal</span> <span className="font-mono text-gray-800">PRS166</span></div>
                                    <div className="flex justify-between"><span>Part Marking</span> <span className="font-mono text-gray-800">PRS162</span></div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <h3 className="font-bold text-yellow-800 text-sm mb-1">Best Used For:</h3>
                            <p className="text-sm text-yellow-900">
                                Final release reviews where adherence to internal document control is critical. It is faster than the consolidated check because it has a narrower focus.
                            </p>
                        </div>
                    </div>
                );
            case 'checker_general':
                return (
                    <div className="space-y-6">
                        <div className="bg-teal-50 border-l-4 border-teal-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Checker - General</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Senior Mechanical Engineer</p>
                        </div>

                        <p className="text-gray-700">
                             Evaluates the drawing purely based on industry-standard engineering principles (ASME Y14.5, ISO) and Design for Manufacturing (DFM) logic. It does not know or care about specific Veeco document numbers.
                        </p>

                        <MermaidDiagram code={`
graph LR
    A["PDF"] --> B["Check Completeness"]
    A --> C["Check GD&T Syntax"]
    A --> D["Check Tolerances"]
    B --> E["Report"]
    C --> E
    D --> E
                        `} />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                <h3 className="font-bold text-gray-800 mb-2">What it Checks</h3>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
                                    <li><strong>Dimensioning:</strong> Finding over-constrained or under-constrained features.</li>
                                    <li><strong>GD&T:</strong> Ensures feature control frames follow ASME Y14.5 syntax (e.g., datums defined, valid symbols).</li>
                                    <li><strong>Clarity:</strong> Flags overlapping text, ambiguous notes, or tiny fonts.</li>
                                </ul>
                            </div>

                            <div className="bg-teal-50 p-5 rounded-lg border border-teal-100">
                                <h3 className="font-bold text-teal-800 mb-2">Best Used For:</h3>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-teal-700">
                                    <li>Vendor drawings where Veeco standards don't apply.</li>
                                    <li>Early stage concepts involving off-the-shelf parts.</li>
                                    <li>Quick checks for manufacturing feasibility.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                );
            case 'weight':
                return (
                    <div className="space-y-6">
                        <div className="bg-emerald-50 border-l-4 border-emerald-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Weight Estimation</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Manufacturing Mass Estimator with Adversarial Review</p>
                        </div>

                        <p className="text-gray-700">
                            Estimates the mass of a manufactured part directly from a 2D engineering drawing — no CAD model required.
                            The play runs <strong>SourceOptima Agentic AI</strong>: a fast SourceOptima AI classifier identifies the part topology and material,
                            routes the job to a <strong>bespoke specialist profile</strong> (either hardcoded or dynamically generated from our knowledge base),
                            then iterates between code-execution mass calculation and an adversarial SourceOptima AI reviewer until the result converges
                            and clears every acceptance gate.
                        </p>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-sm">
                            <h3 className="font-bold text-yellow-800 mb-1">Why Not Just Ask Copilot?</h3>
                            <p className="text-yellow-900">
                                A single AI prompt — even in Copilot or any general-purpose assistant — hallucinates mass figures with no grounding.
                                SourceOptima Agentic AI forces the system to <em>compute</em> the answer (Python code execution on geometric primitives)
                                and then defends it against an adversarial reviewer that knows manufacturing processes. The output is a number you
                                can defend, not a guess.
                            </p>
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 mt-4">End-to-End Pipeline</h3>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B["Stage 0: SourceOptima AI Classifier"]
    B --> C{"Stated Weight in Title Block?"}
    C -- "Yes" --> Z["Accept Stated Weight (high confidence)"]
    C -- "No" --> D["Route to Specialist Profile"]
    D --> E{"Profile Exists?"}
    E -- "Hardcoded" --> F["Load Built-in Specialist"]
    E -- "Knowledge Base" --> G["Load Dynamic Specialist"]
    E -- "None" --> H["Generate New Specialist + Cache"]
    F --> I["Phase A: SourceOptima AI Generates Bespoke Prompt"]
    G --> I
    H --> I
    I --> J["SourceOptima AI Code-Execution: Calculate Mass"]
    J --> K["Phase B: Adversarial Debate"]
    K --> L{"Reviewer Agrees?"}
    L -- "No, < 3 rounds" --> M["Recalculate"]
    M --> K
    L -- "Yes / max rounds" --> N["Phase C: Acceptance Gates"]
    N --> O{"Parse / FF / Plausibility / BBox All Pass?"}
    O -- "Yes" --> P["Accepted: Mass + Confidence"]
    O -- "No" --> Q["Accepted with Warnings"]
                        `} />

                        <h3 className="text-lg font-bold text-gray-800 mt-4">The Five Pipeline Stages</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>1. Classify</h4>
                                <p className="text-xs text-gray-600">
                                    A fast SourceOptima AI pass extracts <strong>topology</strong> (e.g. <em>PRISMATIC_MILLED</em>, <em>SHEET_METAL</em>, <em>TURNED</em>),
                                    <strong> material</strong> (alloy + condition), <strong>category</strong>, and any <strong>stated weight</strong> printed in the title block.
                                    If a stated weight is found and trusted, the pipeline short-circuits and returns it with high confidence.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>2. Specialist Routing</h4>
                                <p className="text-xs text-gray-600">
                                    The classified topology routes the job to a <strong>bespoke prompt profile</strong>. Built-in specialists exist
                                    for the common cases; novel topologies trigger dynamic profile generation, which is then <strong>cached in our knowledge base</strong>
                                    so subsequent runs of the same shape class reuse it.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>3. Calculate (Phase A)</h4>
                                <p className="text-xs text-gray-600">
                                    SourceOptima AI generates a <strong>bespoke prompt</strong> for this exact part, then runs <strong>Python code execution</strong>
                                    to decompose the geometry into primitives (boxes, cylinders, sheet panels), apply density, and produce an initial mass
                                    along with a transparent decomposition table.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>4. Expert Debate (Phase B)</h4>
                                <p className="text-xs text-gray-600">
                                    A <strong>SourceOptima AI adversarial reviewer</strong> challenges the calculated result over 2–3 rounds — flagging missed features,
                                    unrealistic tolerances, or wrong densities. <strong>Jump-guards</strong> prevent wild swings, <strong>oscillation
                                    detection</strong> stops infinite loops, and a <strong>mid-debate reclassify</strong> can correct an early
                                    topology mistake.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm md:col-span-2">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>5. Acceptance Gates (Phase C)</h4>
                                <p className="text-xs text-gray-600 mb-1">
                                    Before a number is returned to the user it must pass <strong>four independent gates</strong>:
                                </p>
                                <ul className="list-disc pl-5 text-xs text-gray-600 space-y-1">
                                    <li><strong>Parse Gate</strong> — final answer must be a parseable mass with units.</li>
                                    <li><strong>Fill-Factor Gate</strong> — implied volume vs. bounding box must be physically realistic.</li>
                                    <li><strong>Mass Plausibility</strong> — within sensible range for the material × envelope.</li>
                                    <li><strong>BBox Consistency</strong> — bounding box dimensions match the drawing's stated dimensions.</li>
                                </ul>
                            </div>
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 mt-4">Inside the Adversarial Debate</h3>
                        <p className="text-sm text-gray-600 -mt-2">
                            A typical 2-round debate looks like this. Each panel is one "turn" — the Calculator proposes a mass,
                            the Reviewer audits it, and they trade revisions until they agree (or hit the round cap).
                        </p>

                        <div className="space-y-3 mt-2">
                            {/* Panel 1 — Calculator opening */}
                            <div className="flex gap-3 items-start">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">🧮</div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-emerald-700 mb-1">Calculator · Round 1</div>
                                    <div className="relative bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tl-none p-4 text-sm text-gray-800 shadow-sm">
                                        <p>"I see a milled aluminum bracket — 6061-T6, density 2.70 g/cm³. Decomposing into a base plate (120×80×8 mm) and a vertical web (60×40×6 mm). Subtracting two M6 thru-holes."</p>
                                        <p className="mt-2 font-mono text-xs text-emerald-800">→ Initial estimate: <strong>0.81 kg</strong> (FF 64%)</p>
                                    </div>
                                </div>
                            </div>

                            {/* Panel 2 — Reviewer critique */}
                            <div className="flex gap-3 items-start flex-row-reverse">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">🔍</div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-orange-700 mb-1 text-right">Reviewer · Round 1</div>
                                    <div className="relative bg-orange-50 border border-orange-200 rounded-2xl rounded-tr-none p-4 text-sm text-gray-800 shadow-sm">
                                        <p>"Hold on — looking at section view A-A on page 2, there's a <strong>30×20 mm pocket</strong> on the back face you missed. Rev B added it. Your decomposition also doesn't account for the chamfer on the leading edge."</p>
                                        <p className="mt-2 font-mono text-xs text-orange-800">⚠ Verdict: <strong>REVISE</strong> — 1 missed feature, ~6% over</p>
                                    </div>
                                </div>
                            </div>

                            {/* Panel 3 — Calculator revises */}
                            <div className="flex gap-3 items-start">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">🧮</div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-emerald-700 mb-1">Calculator · Round 2</div>
                                    <div className="relative bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tl-none p-4 text-sm text-gray-800 shadow-sm">
                                        <p>"You're right — re-running with the back-face pocket subtracted and the 2 mm chamfer applied to the leading edge."</p>
                                        <p className="mt-2 font-mono text-xs text-emerald-800">→ Updated estimate: <strong>0.7742 kg</strong> (FF 61%, Δ −4.4%)</p>
                                    </div>
                                </div>
                            </div>

                            {/* Panel 4 — Reviewer accepts */}
                            <div className="flex gap-3 items-start flex-row-reverse">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">✅</div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-orange-700 mb-1 text-right">Reviewer · Round 2</div>
                                    <div className="relative bg-orange-50 border border-orange-200 rounded-2xl rounded-tr-none p-4 text-sm text-gray-800 shadow-sm">
                                        <p>"Decomposition matches what I see on the drawing. Fill factor is in range for a milled bracket. Mass agrees with my independent recon within 1.2%."</p>
                                        <p className="mt-2 font-mono text-xs text-orange-800">✓ Verdict: <strong>ACCEPT</strong> — converged at round 2</p>
                                    </div>
                                </div>
                            </div>

                            {/* Panel 5 — Gates final */}
                            <div className="flex gap-3 items-start">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-700 text-white flex items-center justify-center font-bold text-lg shadow-sm">🛡️</div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-gray-700 mb-1">Acceptance Gates</div>
                                    <div className="relative bg-gray-50 border border-gray-300 rounded-2xl rounded-tl-none p-4 text-sm text-gray-800 shadow-sm">
                                        <p className="font-mono text-xs">Parse ✓ &nbsp; FF ✓ &nbsp; Plausibility ✓ &nbsp; BBox ✓</p>
                                        <p className="mt-2 text-sm">All 4 gates pass — released to the user with <strong className="text-emerald-700">high confidence</strong>.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-xs text-blue-900 mt-2">
                            <strong>Round caps:</strong> the debate runs <strong>2–3 rounds</strong> max. If the Calculator and Reviewer
                            never agree within tolerance, the result is still released but flagged as <em>medium</em> or <em>low confidence</em>
                            so you know to double-check it.
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 mt-4">What You See in the Terminal Panel</h3>

                        <div className="bg-gray-900 text-emerald-300 font-mono text-xs rounded-lg p-4 overflow-x-auto">
                            <div>&gt; Classify: PRISMATIC_MILLED · 6061-T6 Aluminum</div>
                            <div>&gt; Specialist: prismatic_milled_v3 (cached)</div>
                            <div>&gt; Prompt Gen OK</div>
                            <div>&gt; Calculate: 0.81 kg (initial)</div>
                            <div>&gt; Expert Review: critique - missed pocket on rev B</div>
                            <div>&gt; Iteration 2 · Result: 0.7742 kg</div>
                            <div>&gt; Debate Result: converged</div>
                            <div>&gt; Acceptance: parse · FF · plausibility · bbox — all PASS</div>
                            <div className="text-orange-300">&gt; ✓ Final: 0.7742 kg (high confidence)</div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-emerald-50 p-5 rounded-lg border border-emerald-100">
                                <h3 className="font-bold text-emerald-800 mb-2">Confidence Tiers</h3>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-emerald-700">
                                    <li><strong>High</strong> — stated weight verified, OR converged with all 4 gates clean.</li>
                                    <li><strong>Medium</strong> — accepted with 1+ gate warnings (still usable, flagged in UI).</li>
                                    <li><strong>Low</strong> — partial convergence or unresolved debate after max rounds.</li>
                                </ul>
                            </div>

                            <div className="bg-orange-50 p-5 rounded-lg border border-orange-100">
                                <h3 className="font-bold text-orange-800 mb-2">Best Used For:</h3>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-orange-700">
                                    <li>Early-stage <strong>cost estimation</strong> requiring mass before BOM finalization.</li>
                                    <li><strong>Logistics &amp; freight</strong> planning from drawings only.</li>
                                    <li><strong>Cross-checks</strong> against vendor-stated weights on the title block.</li>
                                    <li><strong>Tariff calculations</strong> needing mass per HTS chapter (pairs with Tariff Analysis).</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mt-4">
                            <h3 className="font-bold text-gray-800 mb-2">How to Use</h3>
                            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                                <li>Select one or more drawings in your workspace.</li>
                                <li>Choose <strong>Weight Estimation</strong> from the play dropdown and run.</li>
                                <li>Watch the <strong>terminal panel</strong> — each pipeline stage streams live (Classify → Specialist → Calculate → Debate → Gates).</li>
                                <li>When complete, the result card shows <strong>mass, material, confidence tier</strong>, and the <strong>decomposition table</strong> the model used.</li>
                                <li>Use the result alongside <strong>Tariff Analysis</strong> or <strong>Cost Reduction</strong> for downstream calculations.</li>
                            </ol>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm">
                            <h3 className="font-bold text-blue-800 mb-1">Performance Notes</h3>
                            <p className="text-blue-900">
                                A typical run takes <strong>4–8 minutes</strong> per drawing depending on complexity (more iterations = more
                                AI calls). Per-org concurrency is <strong>capped at 3 parallel jobs</strong> by default to stay under
                                AI provider rate limits. Each file consumes <strong>3 base credits + 1 per 2 pages</strong>.
                            </p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <FaBook className="text-[#1E5045]" />
                        <span>Training</span>
                    </h1>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === tab.id 
                                ? 'bg-[#1E5045] text-white shadow-sm' 
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <span className="text-lg">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default TrainingDocs;
